import React from 'react'
import { Button, DataTable } from '@/shared/ui'
import type { Column, InvoiceDTO } from '@/shared/api'
import type { PaginationInfo } from '@/shared/ui'
import { formatMoney, invoiceNumberLabel } from './format'
import { InvoiceStatusBadge } from './InvoiceStatusBadge'

interface Props {
  data: InvoiceDTO[]
  loading: boolean
  pagination?: (PaginationInfo & { onPageChange: (page: number) => void }) | undefined
  canConfirm: boolean
  canCancel: boolean
  downloadingId: number | null
  onConfirm: (invoice: InvoiceDTO) => void
  onCancel: (invoice: InvoiceDTO) => void
  onDownloadPdf: (invoice: InvoiceDTO) => void
}

/** Таблица счетов демо-баланса с PDF и действиями по статусу. */
export const BalanceTable: React.FC<Props> = ({
  data,
  loading,
  pagination,
  canConfirm,
  canCancel,
  downloadingId,
  onConfirm,
  onCancel,
  onDownloadPdf,
}) => {
  const columns: Column<InvoiceDTO>[] = [
    {
      key: 'number',
      title: 'Номер',
      render: (_, invoice) => invoiceNumberLabel(invoice),
    },
    {
      key: 'createdAt',
      title: 'Дата',
      render: (_, invoice) => invoice.createdAt || '—',
    },
    {
      key: 'entityName',
      title: 'Юрлицо',
      render: (_, invoice) =>
        invoice.entityName || `Юрлицо #${invoice.legalEntityId}`,
    },
    {
      key: 'amount',
      title: 'Сумма',
      render: (_, invoice) => formatMoney(invoice.amount),
    },
    {
      key: 'status',
      title: 'Статус',
      render: (_, invoice) => (
        <InvoiceStatusBadge
          status={invoice.status}
          statusName={invoice.statusName}
        />
      ),
    },
    {
      key: 'pdfUrl',
      title: 'PDF',
      render: (_, invoice) => (
        <Button
          size="sm"
          variant="secondary"
          onClick={() => onDownloadPdf(invoice)}
          isLoading={downloadingId === invoice.invoiceId}
        >
          PDF
        </Button>
      ),
    },
    {
      key: 'actions',
      title: 'Действия',
      render: (_, invoice) => {
        if (invoice.status !== 1) return '—'
        const actions = (
          <div className="flex gap-2 flex-wrap">
            {canConfirm && (
              <Button size="sm" onClick={() => onConfirm(invoice)}>
                Подтвердить оплату
              </Button>
            )}
            {canCancel && (
              <Button size="sm" variant="danger" onClick={() => onCancel(invoice)}>
                Аннулировать
              </Button>
            )}
          </div>
        )
        return canConfirm || canCancel ? actions : '—'
      },
    },
  ]

  return (
    <DataTable
      data={data}
      columns={columns}
      loading={loading}
      emptyMessage="Счетов с выбранными фильтрами нет"
      pagination={pagination}
    />
  )
}
